import { useEffect, useRef } from "react";
import { fireAlerts, loadAlertPrefs } from "@/lib/tradeAlerts";

/**
 * useTradeAlerts — watches a polled `trades` array and fires vibration/sound
 * alerts when NEW fills appear (by filled_at_ms), respecting per-side prefs.
 *
 * Trades come newest-first from the API. On the first render we only record
 * the latest-seen timestamp (a baseline) so we don't blast an alert for every
 * historical trade on page load. After that, any trade newer than the baseline
 * triggers an alert for its side.
 *
 * @param trades      array of trade objects ({ side, filled_at_ms, ... })
 * @param prefsRef    optional ref holding current prefs (so live setting
 *                    changes take effect without re-subscribing). If omitted,
 *                    prefs are read from localStorage on each fill.
 */
export function useTradeAlerts(trades, prefsRef = null) {
    const lastSeenMs = useRef(null);
    const primed = useRef(false);

    useEffect(() => {
        if (!Array.isArray(trades) || trades.length === 0) return;

        // Determine the newest timestamp in the current batch.
        let maxMs = 0;
        for (const t of trades) {
            const ms = Number(t?.filled_at_ms || t?.ts_ms || 0);
            if (ms > maxMs) maxMs = ms;
        }
        if (maxMs <= 0) return;

        // First batch: set the baseline, don't fire for history.
        if (!primed.current) {
            lastSeenMs.current = maxMs;
            primed.current = true;
            return;
        }

        // Nothing newer than what we've already handled.
        if (maxMs <= (lastSeenMs.current || 0)) return;

        // Fire for every trade newer than the last-seen watermark, oldest→newest
        // so multiple simultaneous fills alert in order.
        const fresh = trades
            .filter((t) => Number(t?.filled_at_ms || t?.ts_ms || 0) > (lastSeenMs.current || 0))
            .sort((a, b) => Number(a?.filled_at_ms || 0) - Number(b?.filled_at_ms || 0));

        const prefs = prefsRef?.current || loadAlertPrefs();
        for (const t of fresh) {
            const side = (t?.side || "").toUpperCase();
            if (side === "BUY" || side === "SELL") {
                fireAlerts(side, prefs);
            }
        }
        lastSeenMs.current = maxMs;
    }, [trades, prefsRef]);
}

export default useTradeAlerts;
